<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'

import CurrentQuotesCard from '@/views/my_garage/components/current_quotes_card.vue'
import ServiceHistoryCard from '@/views/my_garage/components/service_history_card.vue'
import Scrollable from '@/components/scrollable.vue'
import SkeletonLoader from '@/components/ui/skeleton_loader.vue'

// I18n
const { t } = useI18n()

// Stores
const garageStore = useGarageStore()
const modalStore = useModalStore()

// Variables
const isLoading = ref(false)

// Watch for vehicle changes to reload data
watch(() => garageStore.currentVehicle, async (newVehicle, oldVehicle) => {
  if (newVehicle && (!oldVehicle || oldVehicle.id !== newVehicle.id)) {
    isLoading.value = true
    try {
      await garageStore.getUserQuotes()
      await garageStore.getUserServiceHistories()
    } finally {
      isLoading.value = false
    }
  }
})

onMounted(async () => {
  if (garageStore.currentVehicle) {
    isLoading.value = true
    try {
      await garageStore.getUserQuotes()
      await garageStore.getUserServiceHistories()
    } finally {
      isLoading.value = false
    }
  }
})

const addCompletedService = () => {
  modalStore.setModal(modalStore.modalTypeConstants.ADD_COMPLETED_SERVICE)
}

const moveQuoteToServiceHistory = (quote) => {
  modalStore.moveToServiceHistoryQuote = quote;
  modalStore.setModal(modalStore.modalTypeConstants.CONFIRM_MOVE_TO_SERVICE_HISTORY)
}
</script>

<template>
  <!-- Loading Overlay for Moving to Service History -->
  <div v-if="garageStore.isMovingToServiceHistory" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
      <svg class="animate-spin h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-lg font-medium">{{ t('my_garage.moving_to_service_history') }}</p>
    </div>
  </div>

  <!-- Skeleton Loader -->
  <div v-if="isLoading" class="mt-2 space-y-6">
    <!-- Current Quotes Skeleton -->
    <div>
      <div class="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
      <div class="flex space-x-4 overflow-hidden">
        <div v-for="n in 3" :key="n" class="animate-pulse">
          <div class="bg-gray-200 rounded-xl min-w-[300px] w-[300px] min-h-[200px]"></div>
        </div>
      </div>
    </div>
    
    <!-- Service History Skeleton -->
    <div>
      <div class="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
      <div class="flex space-x-4 overflow-hidden">
        <div v-for="n in 2" :key="n" class="animate-pulse">
          <div class="bg-gray-200 rounded-xl min-w-[300px] w-[300px] min-h-[100px]"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Actual Content -->
  <div v-else class="mt-2">
    <div class="flex flex-row items-center space-x-3 pb-1">
      <p class="font-semibold text-xl">{{ t('my_garage.your_current_quotes') }}</p>
    </div>

    <Scrollable :show-scroll="garageStore.currentVehicleQuotes.length > 1">

      <router-link to="/request_a_quote/step/1?reset=true" class="relative flex flex-col justify-center scrollable-card service-history-card rounded-xl min-w-[300px] w-[300px] min-h-[200px] cursor-pointer transition ease-in-out duration-200">
        <div class="w-full rounded-lg justify-center items-center mt-2 px-16">
          <label class="flex flex-col items-center justify-center w-full h-48 rounded-md cursor-pointer completed-service">
            <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Upload Icon" class="h-12 w-12">
            <span class="text-lg font-medium mt-2 text-center">{{ t('header.request_a_quote') }}</span>
          </label>
        </div>
      </router-link>

      <CurrentQuotesCard
        v-for="quote in garageStore.currentVehicleQuotes"
        :key="quote.id"
        :quote="quote"
        @moveToServiceHistory="moveQuoteToServiceHistory"
        type="current"
        class="relative flex flex-col scrollable-card quote-card rounded-xl min-w-[300px] w-[300px] min-h-[200px] cursor-pointer transition ease-in-out duration-200">
      </CurrentQuotesCard>
    </Scrollable>

    <!-- <div class="flex flex-row items-center space-x-3 pb-1">
      <p class="font-semibold text-xl">{{ t('my_garage.your_other_quotes') }}</p>
    </div>

    <Scrollable :show-scroll="garageStore.otherQuotes.length > 1">

     <router-link to="/request_a_quote/step/1?reset=true" class="relative flex flex-col justify-center scrollable-card service-history-card rounded-xl min-w-[300px] w-[300px] min-h-[200px] cursor-pointer transition ease-in-out duration-200">
       <div class="w-full rounded-lg justify-center items-center mt-2 px-16">
         <label class="flex flex-col items-center justify-center w-full h-48 rounded-md cursor-pointer completed-service">
           <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Upload Icon" class="h-12 w-12">
           <span class="text-lg font-medium mt-2 text-center">{{ t('header.request_a_quote') }}</span>
         </label>
       </div>
     </router-link>

      <CurrentQuotesCard
        v-for="quote in garageStore.otherQuotes"
        :key="quote.id"
        :quote="quote"
        @moveToServiceHistory="moveQuoteToServiceHistory"
        type="other"
        class="relative flex flex-col scrollable-card quote-card rounded-xl min-w-[300px] w-[300px] min-h-[200px] cursor-pointer transition ease-in-out duration-200">
      </CurrentQuotesCard>
    </Scrollable> -->

    <div class="flex flex-row items-center space-x-3 pb-1">
      <p class="font-semibold text-xl">{{ t('my_garage.your_service_history') }}</p>
    </div>

    <Scrollable :show-scroll="garageStore.currentVehicleServiceHistory.length > 1">
     <!-- <div class="relative flex flex-col justify-center scrollable-card service-history-card rounded-xl min-w-[300px] w-[300px] min-h-[100px] cursor-pointer transition ease-in-out duration-200">
       <div class="w-full rounded-lg justify-center items-center mt-2 px-16">
         <label
           class="flex flex-col items-center justify-center w-full rounded-md cursor-pointer completed-service"
           @click="addCompletedService">
           <img src="../../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Upload Icon"
                class="h-12 w-12">
           <span class="text-lg font-medium mt-2 text-center">{{ t('my_garage.add_completed_service') }}</span>
         </label>
       </div>
      </div> -->

      <ServiceHistoryCard
        v-for="service_history in garageStore.currentVehicleServiceHistory"
        :key="service_history.id"
        :service_history="service_history"
        class="relative flex flex-col scrollable-card service-history-card rounded-xl min-w-[300px] w-[300px] min-h-[100px] cursor-pointer transition ease-in-out duration-200">
      </ServiceHistoryCard>
    </Scrollable>

  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/my_garage';
</style>
